/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

// Turns an activity from /api/v1/activities into what the "My Activity" design shows: a title, an icon or
// avatar, and a sentence whose subject links to the item. Ported from the AngularJS activity-list-item.

export type ActivityTone = 'neutral' | 'brand' | 'error' | 'success' | 'warning';

export interface ActivityPart {
  text: string;
  // In-app link (ui-router state) or tester app link.
  state?: string;
  params?: Record<string, unknown>;
  href?: string;
}

export interface ActivityView {
  title: string;
  subtitle?: string;
  icon?: string;
  tone?: ActivityTone;
  avatar?: string;
  date: string;
  parts: ActivityPart[];
  // Makes the title a link, e.g. to the community profile of the person the activity is about.
  titleLink?: { state: string; params: Record<string, unknown> };
}

export interface ActivityContext {
  currentUserId?: number;
  testerAppUrl: string;
  badgesById: Record<string, { name: string; level: number; icon: string }>;
}

const BUG_CHANGE_REASONS: Record<number, string> = {
  1: 'Out of scope',
  2: 'Works as designed',
  3: 'Duplicate',
  4: 'Need more info',
  5: 'Other',
  6: 'Did Not Follow Instructions',
};

const text = (value: string): ActivityPart => ({ text: value });
const link = (value: string, state: string, params: Record<string, unknown>): ActivityPart => ({ text: value, state, params });

const articleLink = (item: any, label = item.title, extra = {}) => link(label, 'singlearticle', { articleId: item.slug, ...extra });
const statusLink = (item: any, label: string, extra = {}) => link(label, 'singlestatus', { statusId: item.id, ...extra });
const topicLink = (item: any, label = item.title, page = 1, extra = {}) => link(label, 'singlediscussionpage', { page, discussionId: item.id, ...extra });
const courseLink = (item: any, label = item.title, extra = {}) => link(label, 'singlecourse', { courseId: item.slug, ...extra });

// Strips HTML from slot descriptions, keeping a space between paragraphs.
export function plainText(html: string): string {
  const normalized = (html || '').replace(/<\/p><p>/gi, '</p> <p>');
  return new DOMParser().parseFromString(normalized, 'text/html').body.textContent || '';
}

export function testerAppLink(activity: any, testerAppUrl: string): string {
  const { item } = activity;
  switch (item?.type) {
    case 'utest30/requirement_survey_response':
      return `${testerAppUrl}/requirement-survey/${item.requirement_survey.id}`;
    case 'utest30/test_cycle':
      return `${testerAppUrl}/testcycles/${item.id}/`;
    case 'utest30/slot_waitlist_event_history':
      return `${testerAppUrl}/testcycles/${item.test_cycle_id}/slots`;
    case 'utest30/bug_message':
      return item.bug
        ? `${testerAppUrl}/testcycles/${item.bug.test_cycle.id}/issues/${item.bug.id}/discussion`
        : `${testerAppUrl}/testcycles/${item.test_run_result.test_cycle.id}/testcases/${item.entity_id}/discussion`;
    case 'utest30/bug_change_progression':
      return `${testerAppUrl}/testcycles/${item.bug.test_cycle.id}/issues/${item.bug.id}`;
    case 'utest30/test_run_result_approval_change_progression':
      return `${testerAppUrl}/testcycles/${item.test_run_result.test_cycle.id}/testcases/${item.test_run_result.id}`;
    default:
      return '#';
  }
}

// Community activities are about a person, so they show that person's avatar, name and title.
function byPerson(activity: any, parts: ActivityPart[]): Partial<ActivityView> {
  const { user } = activity;
  return {
    title: user.name,
    titleLink: user.slug ? { state: 'profile.about', params: { profileId: user.slug } } : undefined,
    subtitle: user.user_title || user.country?.name,
    avatar: user.avatar_url,
    parts,
  };
}

function yours(owner: any, ctx: ActivityContext, other: string): string {
  return owner?.id === ctx.currentUserId ? 'your' : other;
}

function commentParts(activity: any, ctx: ActivityContext): ActivityPart[] | null {
  const c = activity.commentable;
  switch (c.type) {
    case 'article':
      return [text(`Commented on ${yours(c.user, ctx, 'an')} article: `), articleLink(c)];
    case 'status':
      return [text('Commented on '), statusLink(c, `${yours(c.user, ctx, 'a')} status`)];
    case 'topic':
      return [text(`Commented on ${yours(c.user, ctx, 'a')} forum post: `), topicLink(c)];
    case 'course':
      return [text(`Commented on ${yours(c.user, ctx, 'a')} course: `), courseLink(c)];
    default:
      return null;
  }
}

function likedCommentParts(comment: any): ActivityPart[] | null {
  const c = comment.commentable;
  const anchor = { '#': `comment_${comment.id}` };
  switch (c.type) {
    case 'article':
      return [text('Liked your '), articleLink(c, 'comment', { comments: comment.page, ...anchor }), text(' on the article: '), articleLink(c)];
    case 'topic':
      return [text('Liked your '), topicLink(c, 'comment', comment.page, anchor), text(' on the forum post: '), topicLink(c)];
    case 'course':
      return [text('Liked your '), courseLink(c, 'comment', { comments: comment.page, ...anchor }), text(' on the course: '), courseLink(c)];
    case 'status':
      return [text('Liked your '), statusLink(c, 'comment', { comments: comment.page, ...anchor }), text(' on '), statusLink(c, 'a status')];
    default:
      return null;
  }
}

function likeParts(activity: any): ActivityPart[] | null {
  const { item } = activity;
  switch (item.type) {
    case 'status':
      return [text('Liked '), statusLink(item, 'your status')];
    case 'article':
      return [text('Liked your article: '), articleLink(item)];
    case 'topic':
      return [text('Liked your forum post: '), topicLink(item)];
    case 'course':
      return [text('Liked your course: '), courseLink(item)];
    case 'comment':
      return likedCommentParts(item);
    default:
      return null;
  }
}

function mentionParts(activity: any): ActivityPart[] | null {
  const { item } = activity;
  if (item.type === 'article') return [text('Mentioned you in '), articleLink(item, 'an article')];
  if (item.type === 'status') return [text('Mentioned you in '), statusLink(item, 'a status')];
  if (item.type !== 'comment') return null;
  const c = item.commentable;
  const target = {
    article: () => articleLink(c, 'a comment'),
    status: () => statusLink(c, 'a comment'),
    topic: () => topicLink(c, 'a forum post'),
    course: () => courseLink(c, 'a comment'),
  }[c.type];
  return target ? [text('Mentioned you in '), target()] : null;
}

const BUG_STATUSES: Record<number, { title: string; icon: string; tone: ActivityTone; outcome?: string }> = {
  2: {
    title: 'Approved', icon: 'bug-success', tone: 'success', outcome: ' has been approved',
  },
  3: {
    title: 'Rejected', icon: 'bug-x', tone: 'error', outcome: ' has been rejected',
  },
  4: {
    title: 'Disputed', icon: 'bug-x', tone: 'warning', outcome: ' has been disputed',
  },
  8: {
    title: 'Bug Discarded', icon: 'bug-x', tone: 'neutral', outcome: ' was discarded',
  },
  9: {
    title: 'Info Requested', icon: 'help-circle', tone: 'warning', outcome: ' has had more information requested',
  },
  10: {
    title: 'Under Review', icon: 'bug-neutral', tone: 'neutral', outcome: ' is under review',
  },
  11: {
    title: 'Pending', icon: 'bug-neutral', tone: 'neutral', outcome: ' has been set to pending',
  },
  12: {
    title: 'Pending', icon: 'bug-neutral', tone: 'neutral', outcome: ' has been set to pending',
  },
  14: {
    title: 'Customer Rejected',
    icon: 'bug-warning',
    tone: 'warning',
    outcome: ' has been rejected by the customer and is now under Testing Services review. You\'ll be notified once a final decision is made',
  },
  15: {
    title: 'Rejected Paid', icon: 'bug-warning', tone: 'warning', outcome: ' has been rejected but paid after review. No impact on your rating',
  },
};

function bugView(activity: any, href: string): Partial<ActivityView> | null {
  const { item } = activity;
  const status = BUG_STATUSES[item.bug_status_id];
  if (!status) return null;
  const reason = BUG_CHANGE_REASONS[item.bug_change_reason_id];
  const withReason = [3, 15].includes(item.bug_status_id) && reason;
  return {
    title: withReason ? `${status.title} (${reason})` : status.title,
    icon: status.icon,
    tone: status.tone,
    parts: [
      text('Your bug report: '),
      { text: `Bug Report ${item.bug.id} - ${item.bug.subject}`, href },
      ...(status.outcome ? [text(status.outcome)] : []),
    ],
  };
}

const TEST_CASE_STATUSES: Record<number, { title: string; icon: string; tone: ActivityTone; outcome?: string }> = {
  2: {
    title: 'Approved', icon: 'file-check', tone: 'success', outcome: ' has been approved',
  },
  3: {
    title: 'Rejected', icon: 'file', tone: 'error', outcome: ' has been rejected',
  },
  6: {
    title: 'Info Requested', icon: 'help-circle', tone: 'warning', outcome: ' has had more information requested',
  },
};

const TEST_CYCLE_STATUSES: Record<number, { title: string; icon: string; tone: ActivityTone; outcome: string }> = {
  2: {
    title: 'Activated', icon: 'file', tone: 'brand', outcome: ' has been activated',
  },
  3: {
    title: 'Locked', icon: 'file-lock', tone: 'error', outcome: ' has been locked',
  },
  6: {
    title: 'Pending Activation', icon: 'file', tone: 'warning', outcome: ' is pending activation',
  },
};

function projectView(activity: any, ctx: ActivityContext): Partial<ActivityView> | null {
  const { item } = activity;
  const href = testerAppLink(activity, ctx.testerAppUrl);
  switch (activity.type) {
    case 'test_cycle_status_activity': {
      const status = TEST_CYCLE_STATUSES[item.test_cycle_status_id];
      return status && {
        title: status.title,
        icon: status.icon,
        tone: status.tone,
        parts: [text('The test cycle: '), { text: item.name, href }, text(status.outcome)],
      };
    }
    case 'test_cycle_invite_activity': {
      const subject = [item.name, `#${item.id}`, item.company_name, item.product_name].filter(Boolean).join(' - ');
      return {
        title: 'Invitation', icon: 'mail', tone: 'neutral', parts: [text('You have been invited to: '), { text: subject, href }],
      };
    }
    case 'requirement_survey_invite_activity':
      return {
        title: 'Requirement Survey',
        icon: 'help-circle',
        tone: 'neutral',
        parts: [text('Complete the survey for: '), { text: item.requirement_survey.name, href }],
      };
    case 'available_waitlist_slot_activity':
      return {
        title: 'Waitlisted Slot',
        icon: 'grid',
        tone: 'neutral',
        parts: [
          text('Slots for: '),
          { text: plainText(item.slot.description), href },
          text(' are now available. Claim the slot as soon as you\'re ready to test.'),
        ],
      };
    case 'removed_from_waitlist_activity':
      return {
        title: 'Removed From Waitlist',
        icon: 'grid',
        tone: 'error',
        parts: [
          text('You were removed from the waitlist for: '),
          { text: plainText(item.slot.description), href },
          text('. The slot criteria changed, so check for a different slot that matches your profile.'),
        ],
      };
    case 'bug_message_activity':
      return {
        title: 'New Message',
        icon: 'message-text-square',
        tone: 'neutral',
        parts: item.bug
          ? [text('New message on your bug report: '), { text: item.bug.subject, href }]
          : [text('New message on your test case: '), { text: item.test_run_result.test_case.name, href }],
      };
    case 'bug_activity':
      return bugView(activity, href);
    case 'test_case_activity': {
      const status = TEST_CASE_STATUSES[item.approval_status_id];
      return status && {
        title: status.title,
        icon: status.icon,
        tone: status.tone,
        parts: [
          text('The test case: '),
          { text: `Test Case ${item.test_run_result.id} - ${item.test_run_result.test_case.name}`, href },
          ...(status.outcome ? [text(status.outcome)] : []),
        ],
      };
    }
    default:
      return null;
  }
}

function communityView(activity: any, ctx: ActivityContext): Partial<ActivityView> | null {
  const { item } = activity;
  switch (activity.type) {
    case 'follow_activity':
      return byPerson(activity, [text('Started following you')]);
    case 'comment_activity': {
      const parts = commentParts(activity, ctx);
      return parts && byPerson(activity, parts);
    }
    case 'like_activity': {
      const parts = likeParts(activity);
      return parts && byPerson(activity, parts);
    }
    case 'at_mention_activity': {
      const parts = mentionParts(activity);
      return parts && byPerson(activity, parts);
    }
    case 'publish_activity':
      return {
        title: 'Article Approved', icon: 'file-check', tone: 'success', parts: [text('Your article '), articleLink(item), text(' has been approved')],
      };
    case 'reject_activity':
      return {
        title: 'Article Rejected', icon: 'file', tone: 'error', parts: [text('Your article '), articleLink(item), text(' has been rejected')],
      };
    case 'badge_activity': {
      const badge = ctx.badgesById[item.badge_type];
      return badge && {
        title: 'Badge Earned',
        avatar: `/utest-static/assets/images/badges/${badge.icon}`,
        parts: [text('You have received the '), link(badge.name, 'gamification', {}), text(` badge and earned ${badge.level} points`)],
      };
    }
    default:
      return null;
  }
}

// Returns null for hidden or unrecognised activities, which the feed skips.
export function toActivityView(activity: any, ctx: ActivityContext): ActivityView | null {
  if (activity.type === 'hidden') return null;
  const view = communityView(activity, ctx) || projectView(activity, ctx);
  return view ? { date: activity.date, ...view } as ActivityView : null;
}
